import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { ChineseService, WritingPractice } from '../../services/chineseService';
import { LessonService, GenericLesson, LessonSummary } from '../../services/lessonServiceTypes';
import PageHeader from '../common/PageHeader';
import GenericPastLessons from '../shared/GenericPastLessons';
import './ChineseWritingCopilot.css';

interface ChineseWritingCopilotProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PREDEFINED_TOPICS = [
  'Daily Life',
  'Travel',
  'Food',
  'Culture',
  'Work',
  'Hobbies',
  'Family',
  'Education',
  'Technology',
  'Health',
  'Custom'
];

type ViewMode = 'current' | 'past-list' | 'past-writing';

// Service wrapper to adapt writing practices to LessonService interface
class WritingPracticeService implements LessonService<GenericLesson> {
  async getPastLessons(limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    const result = await ChineseService.getPastWritingPractices(limit, offset);
    return {
      lessons: result.writings,
      total: result.total
    };
  }

  async getLessonById(lessonId: string): Promise<GenericLesson> {
    const writing = await ChineseService.getWritingPracticeById(lessonId);
    // Convert WritingPractice to GenericLesson format
    return {
      lesson_id: writing.lesson_id,
      lesson_name: writing.lesson_name || `Writing Practice - ${writing.topic}`,
      lesson_objective: writing.lesson_objective || `Writing practice on ${writing.topic}`,
      category: 'chinese_practice_writing',
      created_at: writing.created_at,
      updated_at: writing.updated_at
    };
  }

  async deleteLesson(lessonId: string): Promise<void> {
    await ChineseService.deleteLesson(lessonId);
  }
}

const ChineseWritingCopilot: React.FC<ChineseWritingCopilotProps> = ({
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedTopic, setSelectedTopic] = useState<string>('Daily Life');
  const [customTopic, setCustomTopic] = useState<string>('');
  const [writingText, setWritingText] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState<string>('');
  const [translationResult, setTranslationResult] = useState<{ pinyin: string; translation: string } | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [lessonId, setLessonId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastWriting, setSelectedPastWriting] = useState<WritingPractice | null>(null);
  const [pastWritingsList, setPastWritingsList] = useState<LessonSummary[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [translations, setTranslations] = useState<Array<{ selectedText: string; pinyin: string; translation: string }>>([]);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [intention, setIntention] = useState<string>('');
  const writingPracticeService = new WritingPracticeService();

  // Load latest writing practice on mount
  useEffect(() => {
    loadLatestWriting();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const loadLatestWriting = async () => {
    try {
      const result = await ChineseService.getPastWritingPractices(1);
      if (result.writings.length > 0) {
        const latestWriting = await ChineseService.getWritingPracticeById(result.writings[0].lesson_id);
        // Check if topic is one of the predefined topics
        const isPredefinedTopic = PREDEFINED_TOPICS.includes(latestWriting.topic);
        if (isPredefinedTopic) {
          setSelectedTopic(latestWriting.topic);
          setCustomTopic('');
        } else {
          // It's a custom topic
          setSelectedTopic('Custom');
          setCustomTopic(latestWriting.topic);
        }
        setWritingText(latestWriting.writingText || '');
        setTranslations(latestWriting.translations || []);
        setLessonId(latestWriting.lesson_id);
      }
    } catch (err) {
      console.error('Error loading latest writing:', err);
      // It's okay if there are no writings yet
    }
  };

  const saveWritingPractice = async (topic: string, text: string, currentTranslations?: Array<{ selectedText: string; pinyin: string; translation: string }>) => {
    try {
      const translationsToSave = currentTranslations || translations;
      const result = await ChineseService.saveWritingPractice(
        topic,
        text,
        translationsToSave.length > 0 ? translationsToSave : undefined,
        lessonId || undefined
      );
      setLessonId(result.lesson_id);
      setSavingError(null);
    } catch (err) {
      console.error('Error saving writing practice:', err);
      setSavingError('Failed to save writing practice. Your work is still available locally.');
    }
  };

  const getCurrentTopic = (): string => {
    return selectedTopic === 'Custom' ? customTopic.trim() : selectedTopic;
  };

  const handleGenerate = async () => {
    const topic = getCurrentTopic();
    
    if (!topic) {
      setError('Please select or enter a topic.');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      setSavingError(null);

      const intentionValue = intention.trim() || undefined;
      const result = await ChineseService.continueWriting(topic, writingText, intentionValue);
      
      // Append the next sentence to existing text
      const separator = writingText.trim() && !writingText.trim().match(/[。！？]$/) ? '。' : '';
      const newText = writingText.trim() 
        ? `${writingText.trim()}${separator}${result.nextSentence}`
        : result.nextSentence;
      
      setWritingText(newText);
      
      // Clear intention after successful generation
      setIntention('');
      
      // Auto-save after generating text
      await saveWritingPractice(topic, newText);
    } catch (err) {
      console.error('Error generating next sentence:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate next sentence. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setWritingText('');
    setError(null);
    setTranslationResult(null);
    setSelectedText('');
    setTranslations([]);
    setLessonId(null);
    setIntention('');
  };

  const handleTranslate = async () => {
    if (!textareaRef.current) {
      return;
    }

    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = writingText.substring(start, end).trim();

    if (!text) {
      setError('Please select some Chinese text to translate.');
      return;
    }

    try {
      setIsTranslating(true);
      setError(null);
      setSelectedText(text);

      const result = await ChineseService.translatePhrase(text);
      setTranslationResult(result);
      
      // Save translation to translations array
      const newTranslation = {
        selectedText: text,
        pinyin: result.pinyin,
        translation: result.translation
      };
      const updatedTranslations = [...translations, newTranslation];
      setTranslations(updatedTranslations);
      
      // Auto-save with new translation
      const topic = getCurrentTopic();
      if (topic && writingText) {
        await saveWritingPractice(topic, writingText, updatedTranslations);
      }
    } catch (err) {
      console.error('Error translating phrase:', err);
      setError(err instanceof Error ? err.message : 'Failed to translate phrase. Please try again.');
      setTranslationResult(null);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleOpenPastWritings = async () => {
    try {
      setShowMenu(false);
      // Fetch past writings for the list
      const result = await ChineseService.getPastWritingPractices(100);
      setPastWritingsList(result.writings);
      setViewMode('past-list');
    } catch (err) {
      console.error('Error loading past writings:', err);
      // Open list anyway, it will show empty state
      setPastWritingsList([]);
      setViewMode('past-list');
    }
  };

  const handleSelectPastWriting = async (genericLesson: GenericLesson) => {
    try {
      const writing = await ChineseService.getWritingPracticeById(genericLesson.lesson_id!);
      setSelectedPastWriting(writing);
      setViewMode('past-writing');
    } catch (err) {
      console.error('Error loading past writing:', err);
      setError(err instanceof Error ? err.message : 'Failed to load writing');
    }
  };

  const handleBackToCurrentWriting = () => {
    setViewMode('current');
    setSelectedPastWriting(null);
  };

  const handleResumeWriting = () => {
    if (!selectedPastWriting) return;
    
    // Load the past writing data into current state
    // Check if topic is one of the predefined topics
    const isPredefinedTopic = PREDEFINED_TOPICS.includes(selectedPastWriting.topic);
    if (isPredefinedTopic) {
      setSelectedTopic(selectedPastWriting.topic);
      setCustomTopic('');
    } else {
      // It's a custom topic
      setSelectedTopic('Custom');
      setCustomTopic(selectedPastWriting.topic);
    }
    setWritingText(selectedPastWriting.writingText);
    setTranslations(selectedPastWriting.translations || []);
    setLessonId(selectedPastWriting.lesson_id);
    
    // Clear error states
    setError(null);
    setSavingError(null);
    
    // Switch to current view mode
    setViewMode('current');
    setSelectedPastWriting(null);
  };

  const currentTopic = getCurrentTopic();
  const showCustomInput = selectedTopic === 'Custom';
  const showBackButton = viewMode === 'past-writing';

  return (
    <div className="chinese-writing-copilot-container">
      {/* If in past writings list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && (
        <GenericPastLessons
          service={writingPracticeService}
          onSelectLesson={handleSelectPastWriting}
          onClose={handleBackToCurrentWriting}
          title="Past Writings"
        />
      )}

      {/* Main writing view */}
      {viewMode !== 'past-list' && (
        <>
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button 
              className="page-header-icon-btn chinese-writing-copilot-back-btn"
              onClick={handleBackToCurrentWriting}
              aria-label="Back to current writing"
            >
              ←
            </button>
          ) : (
            <button 
              className="page-header-icon-btn chinese-writing-copilot-back-btn"
              onClick={() => navigate('/chinese')}
              aria-label="Back to Chinese Learning"
            >
              ←
            </button>
          )
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-writing' ? 'Past Writing' : 'Writing Copilot'}
          </h1>
        }
        rightActions={
          viewMode === 'current' ? (
            <div className="chinese-writing-copilot-menu-container" ref={menuRef}>
              <button 
                className="page-header-icon-btn"
                onClick={() => setShowMenu(!showMenu)}
                disabled={isGenerating || isTranslating}
              >
                ⋮
              </button>
              {showMenu && (
                <div className="chinese-writing-copilot-menu-dropdown">
                  <button
                    className="chinese-writing-copilot-menu-item"
                    onClick={handleOpenPastWritings}
                  >
                    📝 Past Writings
                  </button>
                </div>
              )}
            </div>
          ) : null
        }
      />

      <div className="chinese-writing-copilot-content">
        {/* Saving Error Message */}
        {savingError && !isGenerating && !isTranslating && (
          <div className="chinese-error" style={{ background: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
            <p>{savingError}</p>
          </div>
        )}

        {/* Topic Selection - Only show for current writing */}
        {viewMode !== 'past-writing' && (
          <div className="chinese-writing-copilot-section">
            <label htmlFor="topic-select" className="chinese-writing-copilot-section-header">
              /topic
            </label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <select
                id="topic-select"
                className="chinese-writing-copilot-select"
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                disabled={isGenerating}
                style={{ flex: '1', minWidth: '200px' }}
              >
                {PREDEFINED_TOPICS.map(topic => (
                  <option key={topic} value={topic}>{topic}</option>
                ))}
              </select>
              {showCustomInput && (
                <input
                  type="text"
                  className="chinese-writing-copilot-input"
                  placeholder="Enter custom topic..."
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  disabled={isGenerating}
                  style={{ flex: '1', minWidth: '200px' }}
                />
              )}
            </div>
          </div>
        )}

        {/* Show topic for past writing */}
        {viewMode === 'past-writing' && selectedPastWriting && (
          <div className="chinese-writing-copilot-section">
            <div className="chinese-writing-copilot-section-header">/topic</div>
            <div className="chinese-writing-copilot-english-text" style={{ display: 'inline-block', padding: '0.5rem 1rem' }}>
              {selectedPastWriting.topic}
            </div>
          </div>
        )}

        {/* Writing Area - Only show for current writing */}
        {viewMode !== 'past-writing' && (
          <div className="chinese-writing-copilot-section">
            <div className="chinese-writing-copilot-section-header">/writing</div>
            <textarea
              ref={textareaRef}
              className="chinese-writing-copilot-textarea"
              value={writingText}
              onChange={(e) => setWritingText(e.target.value)}
              placeholder="Start writing in Chinese... Click Generate to have AI continue your writing."
              rows={12}
              disabled={isGenerating || isTranslating}
            />
            <div style={{ marginTop: '0.75rem' }}>
              <label htmlFor="intention-input" className="chinese-writing-copilot-section-header">
                /intention
              </label>
              <input
                id="intention-input"
                type="text"
                className="chinese-writing-copilot-input"
                placeholder="Optional: What should the next sentence focus on?"
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                disabled={isGenerating || isTranslating}
                style={{ width: '100%', maxWidth: '100%', marginTop: '0.5rem' }}
              />
            </div>
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem' }}>
              <button
                className="chinese-writing-copilot-btn chinese-writing-copilot-btn-generate"
                onClick={handleGenerate}
                disabled={isGenerating || isTranslating || !currentTopic}
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
              <button
                className="chinese-writing-copilot-btn chinese-writing-copilot-btn-translate"
                onClick={handleTranslate}
                disabled={isGenerating || isTranslating || !writingText}
              >
                {isTranslating ? 'Translating...' : 'Translation'}
              </button>
              {writingText && (
                <button
                  className="chinese-writing-copilot-btn chinese-writing-copilot-btn-reset"
                  onClick={handleReset}
                  disabled={isGenerating || isTranslating}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Show past writing text if viewing past writing */}
        {viewMode === 'past-writing' && selectedPastWriting && (
          <div className="chinese-writing-copilot-section">
            <div className="chinese-writing-copilot-section-header">/writing</div>
            <div className="chinese-writing-copilot-english-text" style={{ whiteSpace: 'pre-wrap', padding: '1rem', background: '#fafafa', borderRadius: '0.375rem' }}>
              {selectedPastWriting.writingText || '(No text written)'}
            </div>
          </div>
        )}

        {/* Translation Result - Only show for current writing */}
        {viewMode !== 'past-writing' && translationResult && (
          <div className="chinese-writing-copilot-section">
            <div className="chinese-writing-copilot-section-header">/translation</div>
            <div className="chinese-writing-copilot-translation-result">
              <div className="chinese-writing-copilot-translation-item">
                <div className="chinese-writing-copilot-translation-label">Pinyin:</div>
                <div className="chinese-writing-copilot-translation-value">{translationResult.pinyin}</div>
              </div>
              <div className="chinese-writing-copilot-translation-item">
                <div className="chinese-writing-copilot-translation-label">Translation:</div>
                <div className="chinese-writing-copilot-translation-value">{translationResult.translation}</div>
              </div>
            </div>
          </div>
        )}

        {/* Show past writing translations if viewing past writing */}
        {viewMode === 'past-writing' && selectedPastWriting && selectedPastWriting.translations && selectedPastWriting.translations.length > 0 && (
          <div className="chinese-writing-copilot-section">
            <div className="chinese-writing-copilot-section-header">/translations</div>
            {selectedPastWriting.translations.map((translation, index) => (
              <div key={index} className="chinese-writing-copilot-translation-result" style={{ marginBottom: '1rem' }}>
                <div className="chinese-writing-copilot-translation-item">
                  <div className="chinese-writing-copilot-translation-label">Selected Text:</div>
                  <div className="chinese-writing-copilot-translation-value">{translation.selectedText}</div>
                </div>
                <div className="chinese-writing-copilot-translation-item">
                  <div className="chinese-writing-copilot-translation-label">Pinyin:</div>
                  <div className="chinese-writing-copilot-translation-value">{translation.pinyin}</div>
                </div>
                <div className="chinese-writing-copilot-translation-item">
                  <div className="chinese-writing-copilot-translation-label">Translation:</div>
                  <div className="chinese-writing-copilot-translation-value">{translation.translation}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resume Writing Button - Only show for past exercises */}
        {viewMode === 'past-writing' && selectedPastWriting && (
          <div className="chinese-writing-copilot-actions" style={{ marginTop: '1rem' }}>
            <button
              className="chinese-writing-copilot-btn chinese-writing-copilot-btn-generate"
              onClick={handleResumeWriting}
              disabled={isGenerating || isTranslating}
            >
              Resume Writing
            </button>
          </div>
        )}

        {/* Loading State */}
        {(isGenerating || isTranslating) && (
          <div className="chinese-writing-copilot-loading">
            <div className="chinese-spinner-large"></div>
            <p className="chinese-loading-text">
              {isGenerating ? 'Generating next sentence...' : 'Translating...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && !isGenerating && !isTranslating && (
          <div className="chinese-error">
            <p>{error}</p>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default ChineseWritingCopilot;

